import AdminCrud from "./AdminCrud";
import { useEffect, useState } from "react";
import { api } from "@/api";
import { Save, RefreshCw, Plus, Trash2, X } from "lucide-react";

export function AdminProducts() {
  const [cats, setCats] = useState({});
  useEffect(() => {
    api.get("/categories").then((r) => {
      const m = {}; (r.data || []).forEach((c) => m[c.id] = c.name);
      setCats(m);
    });
  }, []);
  return (
    <AdminCrud
      endpoint="products"
      title="Products"
      singular="Product"
      defaults={{ slug: "", name: "", category_id: "", short_description: "", description: "", images: [], specifications: [], applications: [], features: [], featured: false, is_published: true, order: 0 }}
      listColumns={[
        { key: "name", label: "Name" },
        { key: "category_id", label: "Category", render: (it) => cats[it.category_id] || "—" },
        { key: "featured", label: "Featured" },
        { key: "is_published", label: "Published" },
      ]}
      fields={[
        { key: "name", label: "Product Name", required: true, autoSlug: true },
        { key: "slug", label: "URL Slug", required: true, placeholder: "e.g. grade-88-hex-bolt" },
        { key: "category_id", label: "Category", type: "category-select", required: true },
        { key: "short_description", label: "Short Description (1 line)", type: "textarea", rows: 2 },
        { key: "description", label: "Full Description", type: "textarea", rows: 8 },
        { key: "images", label: "Images", type: "images" },
        { key: "specifications", label: "Specifications", type: "pairs" },
        { key: "applications", label: "Applications", type: "list", placeholder: "e.g. Automotive Chassis" },
        { key: "features", label: "Key Features", type: "list" },
        { key: "materials", label: "Materials", type: "list" },
        { key: "standards", label: "Standards", type: "list" },
        { key: "brochure_url", label: "Brochure URL (optional)" },
        { key: "featured", label: "Featured product (homepage)", type: "boolean" },
        { key: "is_published", label: "Published (visible on site)", type: "boolean" },
        { key: "order", label: "Display order", type: "number" },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Meta Description", type: "textarea", rows: 2 },
        { key: "seo_keywords", label: "SEO Keywords (comma-separated)" },
      ]}
    />
  );
}

export function AdminCategories() {
  return (
    <AdminCrud
      endpoint="categories"
      title="Product Categories"
      singular="Category"
      defaults={{ slug: "", name: "", description: "", image: "", icon: "Wrench", order: 0 }}
      listColumns={[
        { key: "name", label: "Name" },
        { key: "slug", label: "Slug" },
        { key: "order", label: "Order" },
      ]}
      fields={[
        { key: "name", label: "Name", required: true, autoSlug: true },
        { key: "slug", label: "URL Slug", required: true },
        { key: "description", label: "Description", type: "textarea", rows: 3 },
        { key: "image", label: "Image", type: "image" },
        { key: "icon", label: "Icon (lucide name)" },
        { key: "order", label: "Display Order", type: "number" },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2 },
        { key: "seo_keywords", label: "SEO Keywords" },
      ]}
    />
  );
}

export function AdminIndustries() {
  return (
    <AdminCrud
      endpoint="industries"
      title="Industries"
      singular="Industry"
      defaults={{ slug: "", name: "", description: "", icon: "Factory", image: "", order: 0, is_active: true }}
      listColumns={[{ key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "is_active", label: "Active" }]}
      fields={[
        { key: "name", label: "Name", required: true, autoSlug: true },
        { key: "slug", label: "Slug", required: true },
        { key: "description", label: "Description", type: "textarea", rows: 3 },
        { key: "image", label: "Image", type: "image" },
        { key: "icon", label: "Icon" },
        { key: "order", label: "Order", type: "number" },
        { key: "is_active", label: "Active", type: "boolean" },
      ]}
    />
  );
}

export function AdminTestimonials() {
  return (
    <AdminCrud
      endpoint="testimonials"
      title="Testimonials"
      singular="Testimonial"
      defaults={{ name: "", role: "", company: "", content: "", rating: 5, image: "", is_active: true, order: 0 }}
      listColumns={[{ key: "name", label: "Name" }, { key: "company", label: "Company" }, { key: "is_active", label: "Active" }]}
      fields={[
        { key: "name", label: "Person Name", required: true },
        { key: "role", label: "Role" },
        { key: "company", label: "Company" },
        { key: "content", label: "Quote", type: "textarea", rows: 5, required: true },
        { key: "rating", label: "Rating (1–5)", type: "number" },
        { key: "image", label: "Photo", type: "image" },
        { key: "is_active", label: "Show on site", type: "boolean" },
        { key: "order", label: "Order", type: "number" },
      ]}
    />
  );
}

export function AdminFaqs() {
  return (
    <AdminCrud
      endpoint="faqs"
      title="FAQs"
      singular="FAQ"
      defaults={{ question: "", answer: "", category: "General", order: 0, is_active: true }}
      listColumns={[{ key: "question", label: "Question" }, { key: "category", label: "Category" }, { key: "is_active", label: "Active" }]}
      fields={[
        { key: "question", label: "Question", required: true },
        { key: "answer", label: "Answer", type: "textarea", rows: 4, required: true },
        { key: "category", label: "Category" },
        { key: "order", label: "Order", type: "number" },
        { key: "is_active", label: "Active", type: "boolean" },
      ]}
    />
  );
}

export function AdminCertifications() {
  return (
    <AdminCrud
      endpoint="certifications"
      title="Certifications"
      singular="Certification"
      defaults={{ name: "", description: "", issued_by: "", image: "", order: 0, is_active: true }}
      listColumns={[{ key: "name", label: "Name" }, { key: "issued_by", label: "Issued By" }, { key: "is_active", label: "Active" }]}
      fields={[
        { key: "name", label: "Certification Name", required: true },
        { key: "description", label: "Description", type: "textarea", rows: 3 },
        { key: "issued_by", label: "Issued By" },
        { key: "image", label: "Image", type: "image" },
        { key: "order", label: "Order", type: "number" },
        { key: "is_active", label: "Active", type: "boolean" },
      ]}
    />
  );
}

export function AdminBlog() {
  return (
    <AdminCrud
      endpoint="blog_posts"
      title="Blog Posts"
      singular="Post"
      defaults={{ slug: "", title: "", excerpt: "", content: "", cover_image: "", author: "Durga Editorial", tags: [], is_published: true }}
      listColumns={[{ key: "title", label: "Title" }, { key: "is_published", label: "Published" }]}
      fields={[
        { key: "title", label: "Title", required: true, autoSlug: true },
        { key: "slug", label: "URL Slug", required: true },
        { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
        { key: "content", label: "Content (plain text / markdown)", type: "textarea", rows: 14 },
        { key: "cover_image", label: "Cover Image", type: "image" },
        { key: "author", label: "Author" },
        { key: "tags", label: "Tags", type: "list" },
        { key: "is_published", label: "Published", type: "boolean" },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2 },
        { key: "seo_keywords", label: "SEO Keywords" },
      ]}
    />
  );
}

export function AdminCaseStudies() {
  return (
    <AdminCrud
      endpoint="case_studies"
      title="Case Studies"
      singular="Case Study"
      defaults={{ slug: "", title: "", client: "", industry: "", summary: "", content: "", cover_image: "", images: [], results: [], is_published: true }}
      listColumns={[{ key: "title", label: "Title" }, { key: "client", label: "Client" }, { key: "is_published", label: "Published" }]}
      fields={[
        { key: "title", label: "Title", required: true, autoSlug: true },
        { key: "slug", label: "URL Slug", required: true },
        { key: "client", label: "Client" },
        { key: "industry", label: "Industry" },
        { key: "summary", label: "Summary", type: "textarea", rows: 3 },
        { key: "content", label: "Full Content", type: "textarea", rows: 12 },
        { key: "cover_image", label: "Cover Image", type: "image" },
        { key: "images", label: "Gallery", type: "images" },
        { key: "results", label: "Results", type: "list" },
        { key: "is_published", label: "Published", type: "boolean" },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2 },
      ]}
    />
  );
}

export function AdminPages() {
  return (
    <AdminCrud
      endpoint="pages"
      title="Static Pages"
      singular="Page"
      defaults={{ slug: "", title: "", content: "" }}
      listColumns={[{ key: "title", label: "Title" }, { key: "slug", label: "Slug" }]}
      fields={[
        { key: "title", label: "Title", required: true },
        { key: "slug", label: "URL Slug", required: true, placeholder: "e.g. about, privacy-policy" },
        { key: "content", label: "Content (plain text / markdown-ish)", type: "textarea", rows: 18, required: true },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2 },
        { key: "seo_keywords", label: "SEO Keywords" },
      ]}
    />
  );
}

export function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);

  const reload = () => {
    const url = filter ? `/admin/enquiries?status=${filter}` : "/admin/enquiries";
    api.get(url).then((r) => setItems(r.data || []));
  };
  useEffect(reload, [filter]);

  const setStatus = async (id, status) => { await api.patch(`/admin/enquiries/${id}`, { status }); reload(); };
  const remove = async (id) => { if (window.confirm("Delete enquiry?")) { await api.delete(`/admin/enquiries/${id}`); reload(); setActive(null); } };

  const exportCsv = () => {
    const headers = ["Date", "Name", "Email", "Phone", "Company", "Type", "Product", "Message", "Status", "Source"];
    const rows = items.map((e) => [
      new Date(e.created_at).toISOString(), e.name, e.email, e.phone, e.company, e.type, e.product_name, (e.message || "").replace(/\n/g, " "), e.status, e.source_url,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `enquiries-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1C1C1C]">Enquiries</h1>
          <p className="mt-1 font-body text-[#4B5563]">All customer enquiries received via website forms.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-light !py-2 !px-3" data-testid="enquiries-filter">
            <option value="">All</option><option value="new">New</option><option value="read">Read</option><option value="responded">Responded</option><option value="closed">Closed</option>
          </select>
          <button onClick={reload} className="btn-outline-dark !py-2 !px-3"><RefreshCw size={14} /></button>
          <button onClick={exportCsv} className="btn-primary !py-2" data-testid="enquiries-export">Export CSV</button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB]">
        {items.length === 0 ? <div className="p-10 text-center text-[#4B5563] font-body">No enquiries.</div> : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#E5E7EB]">
              <tr>{["Date", "Name", "Email", "Type", "Status", ""].map((h) => <th key={h} className="px-5 py-3 text-left font-heading font-semibold text-xs uppercase tracking-wider text-[#4B5563]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5] cursor-pointer" onClick={() => { setActive(e); if (e.status === "new") setStatus(e.id, "read"); }} data-testid={`enq-row-${e.id}`}>
                  <td className="px-5 py-4 font-body text-[#4B5563]">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-5 py-4 font-body"><div className="font-heading font-medium text-[#1C1C1C]">{e.name}</div>{e.company && <div className="text-xs text-[#4B5563]">{e.company}</div>}</td>
                  <td className="px-5 py-4 font-body">{e.email}</td>
                  <td className="px-5 py-4 font-body"><span className="chip bg-[#F5F5F5] text-[#1C1C1C]">{e.type}</span></td>
                  <td className="px-5 py-4 font-body"><span className={`chip ${e.status === "new" ? "bg-[#C1121F] text-white" : "bg-[#F5F5F5] text-[#4B5563]"}`}>{e.status}</span></td>
                  <td className="px-5 py-4 text-right"><Trash2 size={14} className="text-[#A3A3A3] hover:text-[#C1121F]" onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex" data-testid="enquiry-detail">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActive(null)} />
          <div className="ml-auto bg-white w-full max-w-xl h-full overflow-y-auto p-8 relative">
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 p-2 text-[#4B5563]"><X size={20} /></button>
            <div className="overline mb-3">{active.type} enquiry</div>
            <h2 className="font-heading font-bold text-2xl text-[#1C1C1C]">{active.name}</h2>
            <div className="mt-2 font-body text-sm text-[#4B5563]">{active.email}{active.phone && ` · ${active.phone}`}</div>
            {active.company && <div className="font-body text-sm text-[#4B5563]">{active.company}</div>}
            {active.product_name && <div className="mt-3 chip bg-[#F5F5F5] text-[#1C1C1C]">Product: {active.product_name}</div>}
            <div className="mt-6 p-4 bg-[#F5F5F5] font-body text-[#1C1C1C] whitespace-pre-wrap">{active.message}</div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {["new", "read", "responded", "closed"].map((s) => (
                <button key={s} onClick={() => setStatus(active.id, s)} className={`px-4 py-2 text-sm font-heading font-semibold border ${active.status === s ? "border-[#C1121F] text-[#C1121F]" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#C1121F]"}`}>
                  Mark {s}
                </button>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <a href={`mailto:${active.email}?subject=Re: Your enquiry to Durga Auto Industries`} className="btn-primary flex-1">Reply via email</a>
              {active.phone && <a href={`tel:${active.phone}`} className="btn-outline-dark">Call</a>}
            </div>
            <div className="mt-4 text-xs text-[#A3A3A3] font-body">Source: {active.source_url || "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const update = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const updateNested = (parent, k, v) => setS((p) => ({ ...p, [parent]: { ...(p[parent] || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  if (!s) return <div className="p-10">Loading…</div>;

  const updateSlide = (i, k, v) => update("hero_slides", s.hero_slides.map((sl, j) => j === i ? { ...sl, [k]: v } : sl));
  const addSlide = () => update("hero_slides", [...(s.hero_slides || []), { image: "", title: "", subtitle: "", cta_text: "Request Quote", cta_link: "/contact" }]);
  const removeSlide = (i) => update("hero_slides", s.hero_slides.filter((_, j) => j !== i));

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-[#F5F5F5] py-4 z-10">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1C1C1C]">Site Settings</h1>
          <p className="mt-1 font-body text-[#4B5563]">All site-wide configuration. Changes are live.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-green-600 font-body">Saved.</span>}
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="settings-save"><Save size={16} /> {saving ? "Saving…" : "Save Settings"}</button>
        </div>
      </div>

      <div className="space-y-8">
        <SettingsCard title="Brand & Identity">
          <Field label="Site Name" value={s.site_name} onChange={(v) => update("site_name", v)} testid="setting-site-name" />
          <Field label="Tagline" value={s.tagline} onChange={(v) => update("tagline", v)} />
          <Field label="Logo URL (optional)" value={s.logo_url} onChange={(v) => update("logo_url", v)} placeholder="https://… or upload" />
        </SettingsCard>

        <SettingsCard title="Contact Info">
          <Field label="Phone" value={s.contact_phone} onChange={(v) => update("contact_phone", v)} testid="setting-phone" />
          <Field label="Email" value={s.contact_email} onChange={(v) => update("contact_email", v)} />
          <Field label="Address" value={s.contact_address} onChange={(v) => update("contact_address", v)} type="textarea" />
          <Field label="WhatsApp Number (digits only, with country code, e.g. 919876543210)" value={s.whatsapp_number} onChange={(v) => update("whatsapp_number", v)} testid="setting-whatsapp" />
          <Field label="Google Maps Embed URL" value={s.map_embed_url} onChange={(v) => update("map_embed_url", v)} type="textarea" />
        </SettingsCard>

        <SettingsCard title="Default SEO">
          <Field label="Default Page Title" value={s.default_seo_title} onChange={(v) => update("default_seo_title", v)} testid="setting-seo-title" />
          <Field label="Default Meta Description" value={s.default_seo_description} onChange={(v) => update("default_seo_description", v)} type="textarea" />
          <Field label="Default Keywords" value={s.default_seo_keywords} onChange={(v) => update("default_seo_keywords", v)} />
          <Field label="GA4 Measurement ID (G-XXXXXXX)" value={s.ga4_measurement_id} onChange={(v) => update("ga4_measurement_id", v)} testid="setting-ga4" />
          <Field label="Google Search Console verification code (without meta tag wrapper)" value={s.gsc_verification} onChange={(v) => update("gsc_verification", v)} />
          <Field label="reCAPTCHA Site Key (v2/v3)" value={s.recaptcha_site_key} onChange={(v) => update("recaptcha_site_key", v)} />
          <Field label="reCAPTCHA Secret Key (server-side)" value={s.recaptcha_secret_key} onChange={(v) => update("recaptcha_secret_key", v)} />
        </SettingsCard>

        <SettingsCard title="Stats Block (homepage)">
          <Field label="Years in business" value={s.stats?.years} onChange={(v) => updateNested("stats", "years", v)} />
          <Field label="Clients" value={s.stats?.clients} onChange={(v) => updateNested("stats", "clients", v)} />
          <Field label="Countries" value={s.stats?.countries} onChange={(v) => updateNested("stats", "countries", v)} />
          <Field label="Certifications" value={s.stats?.certifications} onChange={(v) => updateNested("stats", "certifications", v)} />
        </SettingsCard>

        <SettingsCard title="Social Links">
          {["facebook", "linkedin", "instagram", "twitter", "youtube"].map((k) => (
            <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} value={s.social?.[k]} onChange={(v) => updateNested("social", k, v)} />
          ))}
        </SettingsCard>

        <SettingsCard title="Hero Slides">
          <div className="space-y-6">
            {(s.hero_slides || []).map((sl, i) => (
              <div key={i} className="border border-[#E5E7EB] p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading font-semibold text-[#1C1C1C]">Slide {i + 1}</span>
                  <button onClick={() => removeSlide(i)} className="text-[#C1121F] text-sm">Remove</button>
                </div>
                <Field label="Image URL" value={sl.image} onChange={(v) => updateSlide(i, "image", v)} />
                <Field label="Title" value={sl.title} onChange={(v) => updateSlide(i, "title", v)} />
                <Field label="Subtitle" value={sl.subtitle} onChange={(v) => updateSlide(i, "subtitle", v)} type="textarea" />
                <Field label="CTA Text" value={sl.cta_text} onChange={(v) => updateSlide(i, "cta_text", v)} />
                <Field label="CTA Link" value={sl.cta_link} onChange={(v) => updateSlide(i, "cta_link", v)} />
              </div>
            ))}
            <button onClick={addSlide} className="btn-outline-dark"><Plus size={14} /> Add slide</button>
          </div>
        </SettingsCard>

        <SettingsCard title="Admin Access (Email Allowlist)">
          <p className="text-sm text-[#4B5563] mb-3 font-body">Only these Google emails can sign in to the admin panel. Leave empty to allow ANY signed-in user (not recommended).</p>
          <ListEditor value={s.admin_emails || []} onChange={(v) => update("admin_emails", v)} placeholder="email@company.com" testid="setting-admin-emails" />
        </SettingsCard>
      </div>
    </div>
  );
}

function SettingsCard({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E7EB]">
      <div className="px-6 py-4 border-b border-[#E5E7EB]">
        <h2 className="font-heading font-bold text-lg text-[#1C1C1C]">{title}</h2>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-heading font-semibold text-[#4B5563] mb-2">{label}</label>
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="input-light" placeholder={placeholder} data-testid={testid} />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-light" placeholder={placeholder} data-testid={testid} />
      )}
    </div>
  );
}

function ListEditor({ value, onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  return (
    <div data-testid={testid}>
      <div className="flex gap-2">
        <input type="email" value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className="input-light" />
        <button type="button" onClick={() => { if (v.trim()) { onChange([...(value || []), v.trim()]); setV(""); } }} className="btn-outline-dark px-4">Add</button>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2">
        {(value || []).map((e, i) => (
          <li key={i} className="inline-flex items-center gap-2 bg-[#F5F5F5] px-3 py-1.5 text-sm font-body">
            {e}
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-[#C1121F]"><X size={12} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
